import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { Modal } from '@/pages/operacio/comu';

import { CanvisCtx as Ctx, useCanvisPendents } from './canvisContext';

export function CanvisProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('administracio');
  const [registres, setRegistres] = useState<Record<string, number>>({});
  const [accio, setAccio] = useState<(() => void) | null>(null);
  const pendents = Object.values(registres).some((n) => n > 0);
  const pendentsRef = useRef(pendents);
  useEffect(() => {
    pendentsRef.current = pendents;
  }, [pendents]);

  const registra = useCallback((id: string, n: number) => {
    setRegistres((r) => {
      if ((r[id] ?? 0) === n) return r;
      const nou = { ...r };
      if (n > 0) nou[id] = n;
      else delete nou[id];
      return nou;
    });
  }, []);

  const demana = useCallback((a: () => void) => {
    if (!pendentsRef.current) a();
    else setAccio(() => a);
  }, []);

  useEffect(() => {
    if (!pendents) return;
    const avisa = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisa);
    return () => window.removeEventListener('beforeunload', avisa);
  }, [pendents]);

  const valor = useMemo(() => ({ pendents, registra, demana }), [pendents, registra, demana]);
  return (
    <Ctx.Provider value={valor}>
      {children}
      {accio ? (
        <Modal
          titol={t('marc.canvis.sortirTitol')}
          icona={<Icona nom="exclamationmark-triangle" className="c-orange icon-20" />}
          onTanca={() => setAccio(null)}
          peu={
            <>
              <button type="button" className="btn btn--s" onClick={() => setAccio(null)}>
                {t('marc.canvis.sortirContinua')}
              </button>
              <button
                type="button"
                className="btn btn--danger btn--s"
                onClick={() => {
                  const a = accio;
                  // Els registres es buiden en desmuntar-se l'editor; mentrestant no tornem a demanar.
                  setRegistres({});
                  pendentsRef.current = false;
                  setAccio(null);
                  a();
                }}
              >
                {t('marc.canvis.sortirDescarta')}
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t('marc.canvis.sortirText')}</p>
        </Modal>
      ) : null}
    </Ctx.Provider>
  );
}

/**
 * Barra fosca enganxada a la part inferior amb el nombre de canvis, «Descarta» i «Desa».
 * Només es mostra si `n > 0`. Registra els canvis per al guardià de navegació.
 */
export function BarraCanvis({
  n,
  nom,
  onDesa,
  onDescarta,
  desant,
  desactivat,
  textDesa,
  children,
}: {
  n: number;
  /** Nom de l'element editat («Caiguda de servei»). */
  nom?: string;
  onDesa: () => void;
  onDescarta: () => void;
  desant?: boolean;
  /** Desa desactivat (p. ex. formulari invàlid). */
  desactivat?: boolean;
  textDesa?: string;
  /** Missatge addicional (error de validació…). */
  children?: ReactNode;
}) {
  const { t } = useTranslation('administracio');
  useCanvisPendents(n);
  if (n <= 0) return null;
  return (
    <div
      className="barra-canvis"
      role="region"
      aria-label={t('marc.canvis.pendents', { count: n })}
    >
      <i className="barra-canvis__punt" aria-hidden="true" />
      <span className="barra-canvis__text">
        {nom
          ? t('marc.canvis.pendentsA', { count: n, nom })
          : t('marc.canvis.pendents', { count: n })}
      </span>
      {children ? <span className="barra-canvis__extra">{children}</span> : null}
      <span className="spacer" />
      <button type="button" className="btn btn--s barra-canvis__descarta" onClick={onDescarta}>
        {t('marc.canvis.descarta')}
      </button>
      <button
        type="button"
        className="btn btn--s barra-canvis__desa"
        onClick={onDesa}
        disabled={desant || desactivat}
      >
        <Icona nom="checkmark" />
        {desant ? t('marc.canvis.desant') : (textDesa ?? t('marc.canvis.desa'))}
      </button>
    </div>
  );
}
